// Authenticated admin shell (ADM-UX-1, Part B).
//
// Owns the desktop sidebar collapse state so the rail and the content grid
// always agree on the current width, and remembers the last preference in
// localStorage. Mobile navigation stays exactly as it was (NavBar hamburger +
// drawer); this only ever affects the desktop grid.
import { Box } from "@chakra-ui/react";
import NavBar from "components/NavBar";
import SideBar, {
  ADMIN_SIDEBAR_WIDTHS,
  persistSidebarCollapsed,
  readStoredSidebarCollapsed,
} from "components/SideBar";
import { useCallback, useEffect, useState, type ReactNode } from "react";

// Exported so the layout width decision is unit-testable without relying on
// computed styles in jsdom.
export const sidebarGridTemplateColumns = (isCollapsed: boolean): string =>
  `${isCollapsed ? ADMIN_SIDEBAR_WIDTHS.collapsed : ADMIN_SIDEBAR_WIDTHS.expanded} minmax(0, 1fr)`;

export const AdminShell = ({ children }: { children: ReactNode }) => {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(readStoredSidebarCollapsed);

  // The mobile drawer is opened by the NavBar hamburger and closed by its
  // overlay, the close button or any navigation inside it.
  const openSidebar = useCallback(() => {
    setSidebarOpen(true);
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((previous) => !previous);
  }, []);

  useEffect(() => {
    persistSidebarCollapsed(isSidebarCollapsed);
  }, [isSidebarCollapsed]);

  return (
    <>
      <NavBar onOpenSideBar={openSidebar} />

      <Box
        data-testid="admin-shell-grid"
        display={{ base: "block", md: "grid" }}
        style={{ gridTemplateColumns: sidebarGridTemplateColumns(isSidebarCollapsed) }}
      >
        {/* Hidden below md: the rail gives way to the drawer at the same
            breakpoint where the NavBar hamburger appears. */}
        <Box display={{ base: "none", md: "grid" }}>
          <SideBar
            isOpen={isSidebarOpen}
            onClose={closeSidebar}
            isCollapsed={isSidebarCollapsed}
            onToggleCollapsed={toggleSidebarCollapsed}
          />
        </Box>

        {children}
      </Box>
    </>
  );
};

export default AdminShell;
